/**
 * @fileoverview Mobile navigation controller for the Header (mega) section.
 *
 * Level 1: the burger toggles `header-mega--nav-open` on the <header>, revealing
 * .header-mega__nav as a full-width panel below the (still-visible) top bar. The
 * bar's real viewport position is measured into --header-mega-nav-top /
 * --header-mega-drawer-top so both panel levels sit flush against it.
 *
 * Level 2: each nav item with children has a [data-subdrawer-open="sub-…"] button
 * that opens a stacked full-screen sub-drawer (.mega-menu__subdrawer). Back returns
 * to level 1; the sub-drawer's close button closes the whole menu.
 *
 * Accordions inside sub-drawers are native <details>, so they need no JS.
 */
(() => {
  const header = document.querySelector(".header-mega");
  if (!header) return;

  const burger = header.querySelector(".header-mega__burger");
  const topbar = header.querySelector(".header-mega__top");
  if (!burger) return;

  // Each sub-drawer (#sub-mega-X) ships with an empty body. A parent block can't
  // read its children's settings in Liquid, so each _mega-link-list leaf renders
  // its own accordion inside the desktop panel (#mega-X); move those accordions
  // into the matching drawer body and open the first.
  document.querySelectorAll(".mega-menu__subdrawer").forEach((drawer) => {
    const panel = document.getElementById(drawer.id.replace(/^sub-/, ""));
    const body = drawer.querySelector(".mega-menu__subdrawer-body");
    if (panel && body) {
      panel
        .querySelectorAll("[data-mega-mobile-acc]")
        .forEach((acc) => body.appendChild(acc));
      body.querySelector(".mega-menu__accordion")?.setAttribute("open", "");
    }
    // Re-parent to <body>: the drawers are full-screen `position: fixed` overlays,
    // but a transformed/contained ancestor (common with sticky headers) would
    // become their containing block and clamp them near the top. At <body> level
    // the viewport is always their containing block.
    if (drawer.parentElement !== document.body)
      document.body.appendChild(drawer);
  });

  // Smooth-animate the level-2 accordions — native <details> toggling is instant
  // and CSS can't transition it (a closed <details> doesn't render its content),
  // so animate the panel height by hand and manage the `open` attribute timing.
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  document
    .querySelectorAll(".mega-menu__subdrawer .mega-menu__accordion")
    .forEach((acc) => {
      const summary = acc.querySelector("summary");
      const panel = acc.querySelector(".mega-menu__accordion-panel");
      if (!summary || !panel) return;

      summary.addEventListener("click", (evt) => {
        if (reduceMotion.matches) return; // let the browser toggle it instantly
        evt.preventDefault();
        if (acc.dataset.animating) return;
        acc.dataset.animating = "1";

        if (acc.open) {
          // Collapse: lock the current height, force reflow, then animate to 0.
          panel.style.height = `${panel.scrollHeight}px`;
          panel.getBoundingClientRect();
          panel.style.height = "0px";
          const done = (e) => {
            if (e.propertyName !== "height") return;
            panel.removeEventListener("transitionend", done);
            acc.open = false; // hide content first…
            panel.style.height = ""; // …then release the inline height
            delete acc.dataset.animating;
          };
          panel.addEventListener("transitionend", done);
        } else {
          // Expand: open (renders content), measure, then animate 0 → height.
          acc.open = true;
          const target = panel.scrollHeight;
          panel.style.height = "0px";
          panel.getBoundingClientRect();
          panel.style.height = `${target}px`;
          const done = (e) => {
            if (e.propertyName !== "height") return;
            panel.removeEventListener("transitionend", done);
            panel.style.height = ""; // back to auto so it reflows with content
            delete acc.dataset.animating;
          };
          panel.addEventListener("transitionend", done);
        }
      });
    });

  let scrollY = 0;

  const lockScroll = () => {
    scrollY = window.scrollY;
    document.body.style.top = `-${scrollY}px`;
    document.body.classList.add("header-mega-nav-locked");
  };

  const unlockScroll = () => {
    document.body.classList.remove("header-mega-nav-locked");
    document.body.style.top = "";
    window.scrollTo(0, scrollY);
  };

  const openSubdrawers = () =>
    Array.from(document.querySelectorAll(".mega-menu__subdrawer[data-open]"));

  /** Close every open sub-drawer and return to the level-1 list. */
  const closeSubdrawers = () => {
    openSubdrawers().forEach((d) => {
      d.removeAttribute("data-open");
      d.setAttribute("aria-hidden", "true");
      const trigger = header.querySelector(`[data-subdrawer-open="${d.id}"]`);
      trigger?.setAttribute("aria-expanded", "false");
    });
  };

  /**
   * Publish where the top bar actually sits in the viewport: its bottom edge for
   * the level-1 panel, its top edge for the sub-drawers. Both offsets used to add
   * a hard-coded 42px for the announcement bar, which renders at zero height (its
   * only block is disabled) — so the menu opened 42px low and showed a strip of
   * the page above it.
   *
   * Set on <html> rather than the header: the sub-drawers are re-parented to
   * <body> and would not inherit a property scoped to .header-mega.
   */
  const measureTopbar = () => {
    if (!topbar) return;
    const rect = topbar.getBoundingClientRect();
    const root = document.documentElement.style;
    root.setProperty("--header-mega-drawer-top", `${Math.max(0, rect.top)}px`);
    root.setProperty("--header-mega-nav-top", `${Math.max(0, rect.bottom)}px`);
  };

  const openNav = () => {
    // Add the class first — it drops the action icons, so the bar must be measured
    // afterwards for the panel to sit flush below its final height.
    header.classList.add("header-mega--nav-open");
    measureTopbar();
    burger.setAttribute("aria-expanded", "true");
    lockScroll();
  };

  const closeNav = () => {
    closeSubdrawers();
    header.classList.remove("header-mega--nav-open");
    burger.setAttribute("aria-expanded", "false");
    unlockScroll();
    burger.focus();
  };

  const isNavOpen = () => header.classList.contains("header-mega--nav-open");

  burger.addEventListener("click", () => {
    if (isNavOpen()) closeNav();
    else openNav();
  });

  // Delegate the drilldown / back / close controls. The trigger buttons live in
  // the header; the back/close buttons live in the body-level sub-drawers — so
  // the listener is on document to catch both.
  // Localization selects only close themselves on listbox blur, which touch taps
  // on non-focusable menu areas never fire — so close them from the click
  // delegation below instead: on a tap outside the select, or on its own button
  // when it was already open before the press (a second tap toggles it shut).
  // The select opens itself on `mousedown`, so the open set is snapshotted at
  // `pointerdown` (capture, before that handler) — otherwise the same tap's
  // `click` would close what its `mousedown` just opened.
  let selectsOpenAtPress = [];
  document.addEventListener(
    "pointerdown",
    () => {
      selectsOpenAtPress = Array.from(
        document.querySelectorAll(".header-mega__m-localization .is-open"),
      );
    },
    true,
  );

  document.addEventListener("click", (evt) => {
    selectsOpenAtPress.forEach((sel) => {
      if (typeof sel.hideListbox !== "function" || !sel.isConnected) return;
      const inListbox = evt.target.closest(".custom-select__listbox");
      if (!sel.contains(evt.target) || !inListbox) sel.hideListbox();
    });
    selectsOpenAtPress = [];

    // When a localization select opens, scroll the menu so its row sits at the
    // top and the (tall, in-flow) list unfolds below it. The component's own
    // focus scroll is suppressed (preventScroll in custom-select.js) — it
    // aligned the listbox top instead, hiding the button behind the header.
    const locBtn = evt.target.closest(
      ".header-mega__m-localization .custom-select__btn",
    );
    if (locBtn) {
      const comp = locBtn.closest("custom-select, country-selector");
      const row = locBtn.closest(".localization__selector") || locBtn;
      const scrollRowToTop = () => {
        const nav = header.querySelector(".header-mega__nav");
        if (!nav) return;
        nav.scrollTo({
          top:
            nav.scrollTop +
            (row.getBoundingClientRect().top - nav.getBoundingClientRect().top),
          behavior: reduceMotion.matches ? "auto" : "smooth",
        });
      };
      if (comp && comp.classList.contains("is-open")) {
        // Plain selects (and the country selector once cached) open
        // synchronously — the listbox is already in flow, scroll now.
        scrollRowToTop();
      } else if (locBtn.classList.contains("is-loading")) {
        // Country selector's first open fetches the list first; scrolling now
        // would be clamped (nothing below the row yet). Wait for is-open.
        const obs = new MutationObserver(() => {
          if (!comp.classList.contains("is-open")) return;
          obs.disconnect();
          scrollRowToTop();
        });
        obs.observe(comp, { attributes: true, attributeFilter: ["class"] });
        setTimeout(() => obs.disconnect(), 8000);
      }
    }

    const openBtn = evt.target.closest("[data-subdrawer-open]");
    if (openBtn) {
      const drawer = document.getElementById(
        openBtn.getAttribute("data-subdrawer-open"),
      );
      if (drawer) {
        drawer.setAttribute("data-open", "");
        drawer.setAttribute("aria-hidden", "false");
        openBtn.setAttribute("aria-expanded", "true");
        drawer.querySelector(".mega-menu__subdrawer-back")?.focus();
      }
      return;
    }

    if (evt.target.closest("[data-subdrawer-back]")) {
      const drawer = evt.target.closest(".mega-menu__subdrawer");
      if (drawer) {
        drawer.removeAttribute("data-open");
        drawer.setAttribute("aria-hidden", "true");
        const trigger = header.querySelector(
          `[data-subdrawer-open="${drawer.id}"]`,
        );
        trigger?.setAttribute("aria-expanded", "false");
        trigger?.focus();
      }
      return;
    }

    if (evt.target.closest("[data-subdrawer-close]")) {
      closeNav();
    }
  });

  document.addEventListener("keydown", (evt) => {
    if (evt.key !== "Escape" || !isNavOpen()) return;
    if (openSubdrawers().length) closeSubdrawers();
    else closeNav();
  });

  // Leaving the mobile breakpoint while open: reset to a clean desktop state.
  // Keep in step with the `max-width: 1199.98px` blocks in mega-menu.css.
  const mq = window.matchMedia("(min-width: 1200px)");
  mq.addEventListener("change", (e) => {
    if (e.matches && isNavOpen()) closeNav();
  });

  // Re-measure if the viewport changes while the menu is open (rotation, browser
  // chrome collapsing). Scroll is locked meanwhile, so nothing else moves the bar.
  window.addEventListener("resize", () => {
    if (isNavOpen()) measureTopbar();
  });
})();
